import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Copy, Download, FileText, MoreHorizontal, Pencil, Plus, Rows3, Star, Trash2, X } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Segmentado } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Alternador, AreaSensivel, CampoData, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { Menu } from '../../componentes/menu';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { data, dataHora, nomePessoa, numero, numeroEditavel, paraNumero, percentual, textoLivre } from '../../nucleo/formato';
import { CLASSE_AREA } from './ConcluirAtividades';
import { AvisoConfig, CabecalhoConfig, CLASSE_TABELA_CONFIG, CLASSE_TD, CLASSE_TH } from './config-crm-base';
import { CAMINHO_CONFIGURACOES, caminhoDaNegociacao } from './crm-caminhos';
import { baixarTexto } from './CrmComponentes';
import { csv } from './crm-sugestoes';
import {
  COM_OUTRO,
  conferirRespostas,
  duplicar,
  ESCOLHA,
  faixasParaEnvio,
  limitesPontuacao,
  LIMITE_FAIXAS,
  LIMITE_ITENS,
  LIMITE_OPCOES,
  linhasCsv,
  mover,
  moverOpcao,
  mudarTipo,
  nomeDoArquivo,
  novaFaixa,
  novaPergunta,
  numeracao,
  paraFaixaRascunho,
  paraEnvio,
  paraRascunho,
  pontuacao,
  removerOpcao,
  respostasParaEnvio,
  resumir,
  textoDoValor,
  TIPOS,
  validarEditor,
  validarFaixas,
  type FaixaRascunho,
  type Pergunta,
  type Questionario,
  type Rascunho,
  type RespostaColetada,
  type ResumoPergunta,
  type TipoPergunta,
  type Valor,
} from './questionario-modelo';

/**
 * CRM lote 4 (28/10, N1): Configurações do CRM > Questionários no estilo do Google Formulários. A lista dos questionários; o
 * questionário aberto com as abas Perguntas (o editor em cartões: título e descrição, perguntas de nove tipos, seções, opções
 * com "Outro" e pontos, obrigatória, duplicar, excluir e mover) e Respostas (resumo por pergunta em barras, respostas
 * individuais com o link para a ficha, exportar CSV); e a resposta na ficha da negociação (`ResponderQuestionario`).
 * O questionário aberto fica no endereço (`?q=<id>&aba=respostas`), para voltar e compartilhar.
 */

const CLASSE_CARTAO = 'flex flex-col gap-3 rounded-xl border border-borda bg-sup-1 p-4';
type Aba = 'perguntas' | 'respostas';

export function QuestionariosCrm() {
  const [busca, setBusca] = useSearchParams();
  const aberto = busca.get('q');
  const abrir = (id: string | null, aba?: Aba) =>
    setBusca(
      (b) => {
        const n = new URLSearchParams(b);
        if (id) n.set('q', id);
        else n.delete('q');
        if (aba && aba !== 'perguntas') n.set('aba', aba);
        else n.delete('aba');
        return n;
      },
      { replace: false },
    );
  if (aberto) return <TelaQuestionario id={aberto} aba={busca.get('aba') === 'respostas' && aberto !== 'novo' ? 'respostas' : 'perguntas'} abrir={abrir} />;
  return <ListaQuestionarios abrir={abrir} />;
}

const useQuestionarios = () => useQuery({ queryKey: ['pipeline', 'questionarios'], queryFn: () => get<{ itens: Questionario[] }>('/pipeline/questionarios') });

function ListaQuestionarios({ abrir }: { abrir: (id: string | null, aba?: Aba) => void }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuestionarios();
  const [excluir, setExcluir] = useState<Questionario | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  const acao = useMutation({ mutationFn: (f: () => Promise<unknown>) => f(), onSuccess: recarregar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const criar = (
    <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrir('novo')} data-criar-questionario="">
      {t('crm.configuracoes.questionarios.criar')}
    </Botao>
  );
  // Lote crm7: como na origem, "Criar questionário" no cabeçalho, o aviso que leva à Qualificação e a tabela com o menu "...";
  // o "Em uso" (ligar e desligar) e as respostas ficam do MalgoOS.
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-questionarios="">
      <CabecalhoConfig titulo={t('crm.configuracoes.secoes.questionarios')} intro={<p>{t('crm.cfg.questionarios.sub')}</p>} acao={criar} />
      <AvisoConfig titulo={t('crm.cfg.questionarios.avisoTitulo')}>
        {t('crm.cfg.questionarios.avisoTexto')}{' '}
        <Link to={`${CAMINHO_CONFIGURACOES}/qualificacao`} className="font-medium text-acento-forte hover:underline">
          {t('crm.cfg.questionarios.avisoLink')}
        </Link>
        .
      </AvisoConfig>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-4 py-8 text-center" data-questionarios-vazio="">
              <p className="max-w-lg font-semibold">{t('crm.cfg.questionarios.vazioTitulo')}</p>
              <p className="max-w-lg text-sm text-tinta-2">{t('crm.cfg.questionarios.vazioTexto')}</p>
              {criar}
            </div>
          ) : (
            <div className={`${CLASSE_TABELA_CONFIG} rounded-xl border border-borda bg-sup-1`} role="region" tabIndex={0} aria-label={t('crm.configuracoes.secoes.questionarios')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda">
                    <th className={CLASSE_TH}>{t('crm.cfg.questionarios.colQuestionario')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.questionarios.colPerguntas')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.questionarios.colRespostas')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.questionarios.colEdicao')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.questionarios.colAtivo')}</th>
                    <th className={`${CLASSE_TH} text-right`}>
                      <span className="sr-only">{t('crm.cfg.lista.opcoes')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((x) => (
                    <tr key={x.id} className="border-b border-borda last:border-b-0" data-questionario-config={x.nome}>
                      <td className={CLASSE_TD}>
                        <button type="button" className="text-left font-medium text-acento-forte hover:underline" onClick={() => abrir(x.id)}>
                          {x.nome}
                        </button>
                      </td>
                      <td className={`numero ${CLASSE_TD}`}>{x.perguntas.filter((p) => p.tipo !== 'secao').length}</td>
                      <td className={CLASSE_TD}>
                        <button type="button" className="numero hover:text-acento-forte hover:underline" onClick={() => abrir(x.id, 'respostas')} data-ver-respostas="">
                          {x.respostas ?? 0}
                        </button>
                      </td>
                      <td className={`numero ${CLASSE_TD} text-tinta-3`}>{x.updated_at ? data(x.updated_at) : ''}</td>
                      <td className={CLASSE_TD}>
                        <Alternador marcado={x.ativo} rotulo={t('crm.configuracoes.questionarios.ligar', { nome: x.nome })} aoMudar={(v) => acao.mutate(() => patch(`/pipeline/questionarios/${x.id}`, { ativo: v }))} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <span className="inline-flex">
                          <Menu
                            rotulo={t('crm.cfg.questionarios.opcoes', { nome: x.nome })}
                            gatilho={<MoreHorizontal className="size-5" aria-hidden />}
                            classeGatilho="grid size-11 place-items-center rounded-lg text-tinta-2 hover:bg-sup-2 hover:text-tinta-1 md:size-8"
                            solto={{ direita: true }}
                            data-menu-questionario={x.nome}
                            itens={[
                              { chave: 'editar', rotulo: t('crm.configuracoes.questionarios.editar'), icone: <Pencil className="size-4" />, aoClicar: () => abrir(x.id) },
                              { chave: 'respostas', rotulo: t('crm.cfg.questionarios.verRespostas'), aoClicar: () => abrir(x.id, 'respostas') },
                              { chave: 'excluir', rotulo: t('crm.cfg.questionarios.excluir'), icone: <Trash2 className="size-4" />, perigo: true, separadorAntes: true, aoClicar: () => setExcluir(x) },
                            ]}
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        perigoso
        titulo={t('crm.configuracoes.questionarios.confirmarExcluir', { nome: excluir?.nome ?? '' })}
        texto={t('crm.configuracoes.questionarios.confirmarExcluirTexto')}
        aoConfirmar={() => {
          const x = excluir;
          setExcluir(null);
          if (x) acao.mutate(() => del(`/pipeline/questionarios/${x.id}`));
        }}
      />
    </section>
  );
}

function TelaQuestionario({ id, aba, abrir }: { id: string; aba: Aba; abrir: (id: string | null, aba?: Aba) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const q = useQuestionarios();
  const ID_ABAS = useId();
  // Lote crm7: o cabeçalho da origem no editor ("Questionários" em cima, a seta de voltar à lista e o nome).
  const cabecalho = (titulo: string) => <CabecalhoConfig migalha={t('crm.configuracoes.secoes.questionarios')} voltar={`${CAMINHO_CONFIGURACOES}/questionarios`} titulo={titulo} />;
  if (id === 'novo')
    return (
      <section className="flex min-w-0 flex-col gap-5" data-tela-questionario="novo">
        {cabecalho(t('crm.cfg.questionarios.novo'))}
        <EditorQuestionario atual={null} aoCriar={(novo) => abrir(novo)} />
      </section>
    );
  return (
    <section className="flex min-w-0 flex-col gap-5" data-tela-questionario={id}>
      <Estado consulta={q}>
        {(d) => {
          const atual = d.itens.find((x) => x.id === id);
          if (!atual)
            return (
              <>
                {cabecalho(t('crm.configuracoes.secoes.questionarios'))}
                <p className="rounded-xl border border-dashed border-borda px-4 py-6 text-center text-sm text-tinta-3">{tn('estados.naoEncontrado')}</p>
              </>
            );
          return (
            <>
              {cabecalho(atual.nome)}
              <Abas<Aba>
                idBase={ID_ABAS}
                rotulo={t('crm.quest.abas.rotulo')}
                ativa={aba}
                aoMudar={(a) => abrir(id, a)}
                itens={[
                  { chave: 'perguntas', rotulo: t('crm.quest.abas.perguntas') },
                  { chave: 'respostas', rotulo: `${t('crm.quest.abas.respostas')} (${atual.respostas ?? 0})` },
                ]}
              />
              <PainelAba idBase={ID_ABAS} chave={aba}>
                {aba === 'perguntas' ? <EditorQuestionario key={`${atual.id}-${atual.updated_at ?? ''}`} atual={atual} /> : <RespostasQuestionario q={atual} />}
              </PainelAba>
            </>
          );
        }}
      </Estado>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------- editor

const ICONE_OPCAO: Record<string, (n: number) => ReactNode> = {
  multipla: () => <span className="size-4 shrink-0 rounded-full border-2 border-borda-campo" aria-hidden />,
  caixas: () => <span className="size-4 shrink-0 rounded-sm border-2 border-borda-campo" aria-hidden />,
  lista: (n) => (
    <span className="w-5 shrink-0 text-right text-sm text-tinta-3" aria-hidden>
      {n}.
    </span>
  ),
};

export function EditorQuestionario({ atual, aoCriar }: { atual: Questionario | null; aoCriar?: (id: string) => void }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const idBase = useId();
  const inicial = useMemo(
    () => ({
      nome: atual?.nome ?? '',
      descricao: atual?.descricao ?? '',
      lista: (atual?.perguntas ?? []).map(paraRascunho),
      auto: !!atual?.qualificar_auto,
      faixas: (atual?.faixas ?? []).map((f) => paraFaixaRascunho(f, numeroEditavel)),
    }),
    [atual],
  );
  const [nome, setNome] = useState(inicial.nome);
  const [descricao, setDescricao] = useState(inicial.descricao);
  const [lista, setLista] = useState<Rascunho[]>(() => (inicial.lista.length ? inicial.lista : [novaPergunta(t('crm.quest.opcao', { n: 1 }))]));
  const [ativa, setAtiva] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  // CRM lote 5 (X1): qualificação automática pela pontuação.
  const [auto, setAuto] = useState(inicial.auto);
  const [faixas, setFaixas] = useState<FaixaRascunho[]>(inicial.faixas);
  const alterado =
    !atual ||
    nome !== inicial.nome ||
    descricao !== inicial.descricao ||
    auto !== inicial.auto ||
    JSON.stringify(faixasParaEnvio(faixas, paraNumero)) !== JSON.stringify(faixasParaEnvio(inicial.faixas, paraNumero)) ||
    JSON.stringify(paraEnvio(lista)) !== JSON.stringify(paraEnvio(inicial.lista));
  const numeros = numeracao(lista);
  const totalSecoes = lista.filter((p) => p.tipo === 'secao').length;
  const mudar = (k: string, f: (p: Rascunho) => Rascunho) => setLista((l) => l.map((p) => (p.chave === k ? f(p) : p)));
  const inserir = (p: Rascunho) => {
    setLista((l) => {
      const i = l.findIndex((x) => x.chave === ativa);
      return i < 0 ? [...l, p] : [...l.slice(0, i + 1), p, ...l.slice(i + 1)];
    });
    setAtiva(p.chave);
  };
  const salvar = async () => {
    const e = validarEditor(nome, lista, {
      obrigatorio: t('crm.form.obrigatorio'),
      semOpcoes: t('crm.quest.semOpcoes'),
      opcaoRepetida: (o) => t('crm.quest.opcaoRepetida', { opcao: o }),
      semPerguntas: t('crm.quest.semPerguntas'),
    });
    Object.assign(
      e,
      validarFaixas(auto, paraEnvio(lista), faixas, paraNumero, {
        semPontos: t('crm.quest.qualif.semPontos'),
        semFaixas: t('crm.quest.qualif.erroSemFaixas'),
        numero: t('crm.quest.qualif.erroNumero'),
        invertida: t('crm.quest.qualif.erroInvertida'),
        fora: (min, max) => t('crm.quest.qualif.erroFora', { min: numero(min), max: numero(max) }),
        sobreposta: t('crm.quest.qualif.erroSobreposta'),
      }),
    );
    setErros(e);
    if (Object.keys(e).length) {
      toast.erro(t('crm.quest.corrigir'));
      return;
    }
    const corpo = { nome: nome.trim(), descricao: descricao.trim(), perguntas: paraEnvio(lista), qualificar_auto: auto, faixas: faixasParaEnvio(faixas, paraNumero) };
    setSalvando(true);
    try {
      if (atual) await patch(`/pipeline/questionarios/${atual.id}`, corpo);
      else {
        const id = crypto.randomUUID();
        await post('/pipeline/questionarios', { id, ...corpo });
        aoCriar?.(id);
      }
      toast.sucesso(t('crm.configuracoes.salvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };
  const descartar = () => {
    setNome(inicial.nome);
    setDescricao(inicial.descricao);
    setLista(inicial.lista.length ? inicial.lista : [novaPergunta(t('crm.quest.opcao', { n: 1 }))]);
    setAuto(inicial.auto);
    setFaixas(inicial.faixas);
    setErros({});
  };
  const cheio = lista.length >= LIMITE_ITENS;
  return (
    <form noValidate className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()} data-form-questionario="">
      <div className={`${CLASSE_CARTAO} border-t-4 border-t-acento-forte`}>
        <Rotulo id={`${idBase}-nome`} rotulo={t('crm.quest.titulo')} obrigatorio erro={erros.nome}>
          <Entrada id={`${idBase}-nome`} data-nome-questionario="" maxLength={120} className="text-lg font-semibold" placeholder={t('crm.quest.tituloPadrao')} value={nome} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id={`${idBase}-desc`} rotulo={t('crm.quest.descricao')}>
          <AreaSensivel id={`${idBase}-desc`} rows={2} maxLength={2000} className={CLASSE_AREA} placeholder={t('crm.quest.descricaoPlaceholder')} value={descricao} onChange={(e) => setDescricao(e.target.value)} data-descricao-questionario="" />
        </Rotulo>
      </div>
      {erros.geral && (
        <p role="alert" className="text-sm text-perigo">
          {erros.geral}
        </p>
      )}
      <ol className="flex flex-col gap-3" data-perguntas-editor="">
        {lista.map((p, i) => (
          <li key={p.chave} onFocusCapture={() => setAtiva(p.chave)}>
            {p.tipo === 'secao' ? (
              <CartaoSecao
                p={p}
                n={lista.slice(0, i + 1).filter((x) => x.tipo === 'secao').length}
                total={totalSecoes}
                ativa={ativa === p.chave}
                erro={erros[`p${p.chave}`]}
                mudar={(f) => mudar(p.chave, f)}
                acoes={{ subir: i > 0 ? () => setLista((l) => mover(l, i, -1)) : undefined, descer: i < lista.length - 1 ? () => setLista((l) => mover(l, i, 1)) : undefined, excluir: () => setLista((l) => l.filter((x) => x.chave !== p.chave)) }}
              />
            ) : (
              <CartaoPergunta
                p={p}
                n={numeros[i]!}
                ativa={ativa === p.chave}
                erros={{ texto: erros[`p${p.chave}`], opcoes: erros[`o${p.chave}`] }}
                mudar={(f) => mudar(p.chave, f)}
                acoes={{
                  subir: i > 0 ? () => setLista((l) => mover(l, i, -1)) : undefined,
                  descer: i < lista.length - 1 ? () => setLista((l) => mover(l, i, 1)) : undefined,
                  duplicar: cheio ? undefined : () => setLista((l) => duplicar(l, i)),
                  excluir: lista.length > 1 ? () => setLista((l) => l.filter((x) => x.chave !== p.chave)) : undefined,
                }}
              />
            )}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <Botao icone={<Plus className="size-4" />} disabled={cheio} onClick={() => inserir(novaPergunta(t('crm.quest.opcao', { n: 1 })))} data-adicionar-pergunta="">
          {t('crm.quest.adicionarPergunta')}
        </Botao>
        <Botao icone={<Rows3 className="size-4" />} disabled={cheio} onClick={() => inserir(novaPergunta('', 'secao'))} data-adicionar-secao="">
          {t('crm.quest.adicionarSecao')}
        </Botao>
        {cheio && <span className="text-xs text-tinta-3">{t('crm.quest.limite', { n: LIMITE_ITENS })}</span>}
      </div>
      <ConfigQualificacao perguntas={lista} auto={auto} setAuto={setAuto} faixas={faixas} setFaixas={setFaixas} erros={erros} />
      <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-borda bg-fundo py-3">
        <Botao variante="primario" carregando={salvando} onClick={() => void salvar()} data-salvar-questionario-config="">
          {t('crm.quest.salvar')}
        </Botao>
        {atual && alterado && (
          <Botao variante="fantasma" onClick={descartar} data-descartar-questionario="">
            {t('crm.quest.descartar')}
          </Botao>
        )}
        {atual && alterado && <span className="text-xs text-tinta-3">{t('crm.quest.alterado')}</span>}
      </div>
    </form>
  );
}

/**
 * CRM lote 5 (28/10, X1): a qualificação automática do questionário. Ligada, a resposta na negociação muda as estrelas pela
 * faixa em que a pontuação cair (limites inclusivos, dentro da pontuação possível, sem sobreposição; o servidor confere de novo).
 */
function ConfigQualificacao({
  perguntas,
  auto,
  setAuto,
  faixas,
  setFaixas,
  erros,
}: {
  perguntas: Pick<Pergunta, 'tipo' | 'pontos'>[];
  auto: boolean;
  setAuto: (v: boolean) => void;
  faixas: FaixaRascunho[];
  setFaixas: (f: (l: FaixaRascunho[]) => FaixaRascunho[]) => void;
  erros: Record<string, string>;
}) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const lim = limitesPontuacao(perguntas);
  const mudar = (chave: string, d: Partial<FaixaRascunho>) => setFaixas((l) => l.map((f) => (f.chave === chave ? { ...f, ...d } : f)));
  const ligar = (v: boolean) => {
    setAuto(v);
    if (v && !faixas.length && lim) setFaixas((l) => [...l, novaFaixa(l, lim, paraNumero, numeroEditavel, perguntas)]);
  };
  const estrelas = [1, 2, 3, 4, 5].map((n) => ({ valor: String(n), rotulo: t('crm.quest.qualif.estrelasValor', { count: n }) }));
  return (
    <section className={CLASSE_CARTAO} aria-labelledby={`${id}-t`} data-qualificacao-auto="">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id={`${id}-t`} className="flex items-center gap-2 font-semibold">
          <Star className="size-4 text-acento-forte" aria-hidden />
          {t('crm.quest.qualif.titulo')}
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <span>{t('crm.quest.qualif.ligar')}</span>
          <Alternador marcado={auto} rotulo={t('crm.quest.qualif.ligar')} aoMudar={ligar} />
        </label>
      </div>
      <p className="text-xs text-tinta-3">{t('crm.quest.qualif.ajuda')}</p>
      {auto && (
        <>
          <p className="text-xs text-tinta-2" data-pontuacao-possivel="">
            {lim ? t('crm.quest.qualif.possivel', { min: numero(lim.min), max: numero(lim.max) }) : t('crm.quest.qualif.semPontos')}
          </p>
          {erros.faixas && (
            <p role="alert" className="text-sm text-perigo">
              {erros.faixas}
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {faixas.map((f, i) => (
              <li key={f.chave} className="flex flex-col gap-1" data-faixa={i}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span>{t('crm.quest.qualif.de')}</span>
                  <div className="w-24">
                    <Entrada aria-label={t('crm.quest.qualif.faixaDe', { n: i + 1 })} inputMode="decimal" value={f.min} aria-invalid={!!erros[`f${f.chave}`] || undefined} onChange={(e) => mudar(f.chave, { min: e.target.value })} data-faixa-min="" />
                  </div>
                  <span>{t('crm.quest.qualif.ate')}</span>
                  <div className="w-24">
                    <Entrada aria-label={t('crm.quest.qualif.faixaAte', { n: i + 1 })} inputMode="decimal" value={f.max} aria-invalid={!!erros[`f${f.chave}`] || undefined} onChange={(e) => mudar(f.chave, { max: e.target.value })} data-faixa-max="" />
                  </div>
                  <span aria-hidden>→</span>
                  <div className="w-36">
                    <Selecao aria-label={t('crm.quest.qualif.faixaEstrelas', { n: i + 1 })} value={String(f.estrelas)} opcoes={estrelas} onChange={(e) => mudar(f.chave, { estrelas: Number(e.target.value) })} data-faixa-estrelas="" />
                  </div>
                  <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.qualif.remover', { n: i + 1 })} icone={<X className="size-4" />} onClick={() => setFaixas((l) => l.filter((x) => x.chave !== f.chave))} data-remover-faixa="" />
                </div>
                {erros[`f${f.chave}`] && (
                  <p role="alert" className="text-xs text-perigo">
                    {erros[`f${f.chave}`]}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} disabled={faixas.length >= LIMITE_FAIXAS} onClick={() => setFaixas((l) => [...l, novaFaixa(l, lim, paraNumero, numeroEditavel, perguntas)])} data-adicionar-faixa="">
              {t('crm.quest.qualif.adicionar')}
            </Botao>
            {faixas.length >= LIMITE_FAIXAS && <span className="text-xs text-tinta-3">{t('crm.quest.qualif.limite', { n: LIMITE_FAIXAS })}</span>}
          </div>
        </>
      )}
    </section>
  );
}

interface AcoesCartao {
  subir?: () => void;
  descer?: () => void;
  duplicar?: () => void;
  excluir?: () => void;
}

function BarraAcoes({ n, acoes, secao, children }: { n: number; acoes: AcoesCartao; secao?: boolean; children?: ReactNode }) {
  const { t } = useTranslation('pipeline');
  return (
    <div className="flex flex-wrap items-center justify-end gap-1 border-t border-borda pt-3">
      {acoes.subir && <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.subir', { n })} icone={<ArrowUp className="size-4" />} onClick={acoes.subir} data-subir-pergunta="" />}
      {acoes.descer && <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.descer', { n })} icone={<ArrowDown className="size-4" />} onClick={acoes.descer} data-descer-pergunta="" />}
      {acoes.duplicar && <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.duplicar', { n })} icone={<Copy className="size-4" />} onClick={acoes.duplicar} data-duplicar-pergunta="" />}
      {acoes.excluir && <Botao tamanho="sm" variante="fantasma" aria-label={secao ? t('crm.quest.excluirSecao', { n }) : t('crm.quest.excluir', { n })} icone={<Trash2 className="size-4" />} onClick={acoes.excluir} data-excluir-pergunta="" />}
      {children}
    </div>
  );
}

function CartaoSecao({ p, n, total, ativa, erro, mudar, acoes }: { p: Rascunho; n: number; total: number; ativa: boolean; erro?: string; mudar: (f: (p: Rascunho) => Rascunho) => void; acoes: AcoesCartao }) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  return (
    <div className={`${CLASSE_CARTAO} ${ativa ? 'border-l-4 border-l-acento-forte' : ''}`} data-secao-editor={n}>
      <p className="-mx-4 -mt-4 rounded-t-xl bg-acento-forte px-4 py-1.5 text-xs font-semibold text-sup-1">{t('crm.quest.secaoDe', { n, total })}</p>
      <Rotulo id={`${id}-t`} rotulo={t('crm.quest.secaoTitulo')} obrigatorio erro={erro}>
        <Entrada id={`${id}-t`} maxLength={300} placeholder={t('crm.quest.secaoPlaceholder')} value={p.texto} onChange={(e) => mudar((x) => ({ ...x, texto: e.target.value }))} data-texto-pergunta="" />
      </Rotulo>
      <Rotulo id={`${id}-d`} rotulo={t('crm.quest.descricaoPergunta')}>
        <AreaSensivel id={`${id}-d`} rows={2} maxLength={500} className={CLASSE_AREA} value={p.descricao} onChange={(e) => mudar((x) => ({ ...x, descricao: e.target.value }))} />
      </Rotulo>
      <BarraAcoes n={n} acoes={acoes} secao />
    </div>
  );
}

function CartaoPergunta({ p, n, ativa, erros, mudar, acoes }: { p: Rascunho; n: number; ativa: boolean; erros: { texto?: string; opcoes?: string }; mudar: (f: (p: Rascunho) => Rascunho) => void; acoes: AcoesCartao }) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const opcao1 = t('crm.quest.opcao', { n: 1 });
  return (
    <div className={`${CLASSE_CARTAO} ${ativa ? 'border-l-4 border-l-acento-forte' : ''}`} data-pergunta={n - 1} data-tipo={p.tipo}>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="md:col-span-2">
          <Rotulo id={`${id}-t`} rotulo={`${n}. ${t('crm.quest.pergunta')}`} obrigatorio erro={erros.texto}>
            <Entrada id={`${id}-t`} maxLength={300} placeholder={t('crm.quest.perguntaPlaceholder')} value={p.texto} onChange={(e) => mudar((x) => ({ ...x, texto: e.target.value }))} data-texto-pergunta="" />
          </Rotulo>
        </div>
        <Rotulo id={`${id}-tp`} rotulo={t('crm.quest.tipo')}>
          <Selecao id={`${id}-tp`} data-tipo-pergunta="" value={p.tipo} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: t(`crm.quest.tipos.${x}`) }))} onChange={(e) => mudar((x) => mudarTipo(x, e.target.value as TipoPergunta, opcao1))} />
        </Rotulo>
      </div>
      <Rotulo id={`${id}-d`} rotulo={t('crm.quest.descricaoPergunta')}>
        <EntradaSensivel mascara={null} livre id={`${id}-d`} maxLength={500} value={p.descricao} onChange={(e) => mudar((x) => ({ ...x, descricao: e.target.value }))} data-descricao-pergunta="" />
      </Rotulo>
      {ESCOLHA.has(p.tipo) ? (
        <EditorOpcoes p={p} erro={erros.opcoes} mudar={mudar} />
      ) : p.tipo === 'escala' ? (
        <EditorEscala p={p} mudar={mudar} />
      ) : (
        <p className="border-b border-dotted border-borda-campo pb-1 text-sm text-tinta-3 md:w-1/2" data-previa-resposta="">
          {t(`crm.quest.${({ curta: 'respostaCurta', paragrafo: 'respostaLonga', numero: 'respostaNumero', data: 'respostaData', hora: 'respostaHora' } as Record<string, string>)[p.tipo] ?? 'respostaCurta'}`)}
        </p>
      )}
      <BarraAcoes n={n} acoes={acoes}>
        <span className="mx-2 h-6 border-l border-borda" aria-hidden />
        <label className="flex items-center gap-2 text-sm">
          {t('crm.quest.obrigatoria')}
          <Alternador marcado={p.obrigatoria} rotulo={t('crm.quest.obrigatoriaRotulo', { n })} aoMudar={(v) => mudar((x) => ({ ...x, obrigatoria: v }))} />
        </label>
      </BarraAcoes>
    </div>
  );
}

function EditorOpcoes({ p, erro, mudar }: { p: Rascunho; erro?: string; mudar: (f: (p: Rascunho) => Rascunho) => void }) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const icone = ICONE_OPCAO[p.tipo]!;
  return (
    <div className="flex flex-col gap-2" data-opcoes-editor="">
      <div className="flex items-center justify-end gap-2 text-xs text-tinta-3">
        <span title={t('crm.quest.pontosAjuda')}>{t('crm.quest.pontos')}</span>
      </div>
      <ul className="flex flex-col gap-2">
        {p.opcoes.map((o, i) => (
          <li key={i} className="flex items-center gap-2" data-opcao={i}>
            {icone(i + 1)}
            <Entrada aria-label={t('crm.quest.opcaoRotulo', { n: i + 1 })} maxLength={200} value={o} onChange={(e) => mudar((x) => ({ ...x, opcoes: x.opcoes.map((y, k) => (k === i ? e.target.value : y)) }))} data-texto-opcao="" />
            <div className="w-20 shrink-0">
              <Entrada
                aria-label={t('crm.quest.pontosRotulo', { n: i + 1 })}
                inputMode="decimal"
                value={p.pontos[i] ? numeroEditavel(p.pontos[i]!) : ''}
                placeholder="0"
                onChange={(e) => {
                  const v = paraNumero(e.target.value) ?? 0;
                  mudar((x) => ({ ...x, pontos: x.opcoes.map((_, k) => (k === i ? Math.max(-1000, Math.min(1000, v)) : (x.pontos[k] ?? 0))) }));
                }}
                data-pontos-opcao=""
              />
            </div>
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.subirOpcao', { n: i + 1 })} icone={<ArrowUp className="size-4" />} disabled={i === 0} onClick={() => mudar((x) => moverOpcao(x, i, -1))} />
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.descerOpcao', { n: i + 1 })} icone={<ArrowDown className="size-4" />} disabled={i === p.opcoes.length - 1} onClick={() => mudar((x) => moverOpcao(x, i, 1))} />
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.removerOpcao', { n: i + 1 })} icone={<X className="size-4" />} disabled={p.opcoes.length <= 1} onClick={() => mudar((x) => removerOpcao(x, i))} data-remover-opcao="" />
          </li>
        ))}
        {p.outro && (
          <li className="flex items-center gap-2" data-opcao-outro="">
            {icone(p.opcoes.length + 1)}
            <span className="flex-1 border-b border-dotted border-borda-campo pb-1 text-sm text-tinta-3">{t('crm.quest.outro')}...</span>
            <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.removerOutro')} icone={<X className="size-4" />} onClick={() => mudar((x) => ({ ...x, outro: false }))} />
          </li>
        )}
      </ul>
      {erro && (
        <p id={`${id}-erro`} role="alert" className="text-sm text-perigo">
          {erro}
        </p>
      )}
      {p.opcoes.length < LIMITE_OPCOES && (
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} onClick={() => mudar((x) => ({ ...x, opcoes: [...x.opcoes, t('crm.quest.opcao', { n: x.opcoes.length + 1 })] }))} data-adicionar-opcao="">
            {t('crm.quest.adicionarOpcao')}
          </Botao>
          {COM_OUTRO.has(p.tipo) && !p.outro && (
            <>
              <span className="text-tinta-3">{t('crm.quest.ou')}</span>
              <Botao tamanho="sm" variante="fantasma" onClick={() => mudar((x) => ({ ...x, outro: true }))} data-adicionar-outro="">
                {t('crm.quest.adicionarOutro')}
              </Botao>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EditorEscala({ p, mudar }: { p: Rascunho; mudar: (f: (p: Rascunho) => Rascunho) => void }) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const e = p.escala ?? { min: 1, max: 5, rotulo_min: '', rotulo_max: '' };
  const set = (d: Partial<typeof e>) => mudar((x) => ({ ...x, escala: { ...e, ...d } }));
  return (
    <div className="flex flex-col gap-3" data-escala-editor="">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>{t('crm.quest.escalaDe')}</span>
        <div className="w-20">
          <Selecao aria-label={t('crm.quest.escalaDe')} value={String(e.min)} opcoes={[0, 1].map((n) => ({ valor: String(n), rotulo: String(n) }))} onChange={(ev) => set({ min: Number(ev.target.value) })} data-escala-min="" />
        </div>
        <span>{t('crm.quest.escalaAte')}</span>
        <div className="w-20">
          <Selecao aria-label={t('crm.quest.escalaAte')} value={String(e.max)} opcoes={[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ valor: String(n), rotulo: String(n) }))} onChange={(ev) => set({ max: Number(ev.target.value) })} data-escala-max="" />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Rotulo id={`${id}-min`} rotulo={t('crm.quest.rotuloMin', { n: e.min })}>
          <Entrada id={`${id}-min`} maxLength={60} value={e.rotulo_min} onChange={(ev) => set({ rotulo_min: ev.target.value })} />
        </Rotulo>
        <Rotulo id={`${id}-max`} rotulo={t('crm.quest.rotuloMax', { n: e.max })}>
          <Entrada id={`${id}-max`} maxLength={60} value={e.rotulo_max} onChange={(ev) => set({ rotulo_max: ev.target.value })} />
        </Rotulo>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------- respostas coletadas

function RespostasQuestionario({ q }: { q: Questionario }) {
  const { t } = useTranslation('pipeline');
  const [visao, setVisao] = useState<'resumo' | 'individuais'>('resumo');
  // Val5 (29/10): baixar as respostas em CSV pede `pipeline.exportar`.
  const podeExportar = useSessao().pode('pipeline.exportar');
  const r = useQuery({ queryKey: ['pipeline', 'questionario-respostas', q.id], queryFn: () => get<{ questionario: Questionario; itens: RespostaColetada[]; limite: boolean }>(`/pipeline/questionarios/${q.id}/respostas`) });
  return (
    <Estado consulta={r}>
      {(d) => {
        const perguntas = d.questionario.perguntas;
        const exportar = () =>
          baixarTexto(
            t('crm.quest.arquivoCsv', { nome: nomeDoArquivo(d.questionario.nome) }),
            csv(linhasCsv(d.questionario, d.itens, [t('crm.quest.negociacao'), t('crm.quest.respondidoPor'), t('crm.quest.respondidoEm'), t('crm.quest.pontuacao')], (iso) => dataHora(iso))),
          );
        return (
          <div className="flex flex-col gap-3" data-respostas-questionario="">
            <div className={`${CLASSE_CARTAO} sm:flex-row sm:items-center`}>
              <p className="flex-1 text-xl font-semibold" data-total-respostas={d.itens.length}>
                {t('crm.quest.respostasTotal', { count: d.itens.length })}
              </p>
              {d.itens.length > 0 && (
                <>
                  <Segmentado
                    rotulo={t('crm.quest.visao')}
                    valor={visao}
                    aoMudar={setVisao}
                    opcoes={[
                      { valor: 'resumo', rotulo: t('crm.quest.resumo') },
                      { valor: 'individuais', rotulo: t('crm.quest.individuais') },
                    ]}
                  />
                  {podeExportar && (
                    <Botao icone={<Download className="size-4" />} onClick={exportar} data-exportar-respostas="">
                      {t('crm.quest.exportar')}
                    </Botao>
                  )}
                </>
              )}
            </div>
            {d.limite && <p className="text-xs text-tinta-3">{t('crm.quest.limiteRespostas', { n: d.itens.length })}</p>}
            {!d.itens.length ? (
              <p className="rounded-xl border border-dashed border-borda px-4 py-8 text-center text-sm text-tinta-3">{t('crm.quest.semRespostas')}</p>
            ) : visao === 'resumo' ? (
              perguntas.map((p) => (p.tipo === 'secao' ? <h3 key={p.id} className="mt-2 text-base font-semibold">{p.texto}</h3> : <CartaoResumo key={p.id} p={p} resumo={resumir(p, d.itens)} />))
            ) : (
              <Individuais perguntas={perguntas} itens={d.itens} />
            )}
          </div>
        );
      }}
    </Estado>
  );
}

const pctTexto = (x: number) => `${percentual(x * 100, x * 100 < 10 && x > 0 ? 1 : 0)}%`;

function Barras({ linhas }: { linhas: { rotulo: string; n: number; pct: number }[] }) {
  const { t } = useTranslation('pipeline');
  return (
    <ul className="flex flex-col gap-2" data-barras="">
      {linhas.map((o) => (
        <li key={o.rotulo} className="grid grid-cols-3 items-center gap-3 text-sm" aria-label={t('crm.quest.barra', { opcao: o.rotulo, n: o.n, pct: pctTexto(o.pct) })}>
          <span className="truncate text-tinta-2" title={o.rotulo}>
            {textoLivre(o.rotulo)}
          </span>
          <span className="col-span-2 flex items-center gap-2">
            <span className="h-3 flex-1 overflow-hidden rounded-full bg-sup-3" aria-hidden>
              <span className="block h-full rounded-full bg-acento-forte" style={{ width: `${Math.round(o.pct * 1000) / 10}%` }} />
            </span>
            <span className="w-24 shrink-0 text-right text-xs text-tinta-3 tabular-nums">
              {o.n} ({pctTexto(o.pct)})
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

const MAX_TEXTOS = 50;
/** Inteiro sem casas; fração com duas. */
const casas = (v: number | null) => (v === null || Number.isInteger(v) ? 0 : 2);

function CartaoResumo({ p, resumo }: { p: Pergunta; resumo: ResumoPergunta }) {
  const { t } = useTranslation('pipeline');
  const textos = (l: string[]) => (
    <>
      <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
        {l.slice(0, MAX_TEXTOS).map((x, i) => (
          <li key={i} className="rounded-lg bg-sup-2 px-3 py-1.5 text-sm">
            {p.tipo === 'data' ? data(x) : textoLivre(x)}
          </li>
        ))}
      </ul>
      {l.length > MAX_TEXTOS && <p className="text-xs text-tinta-3">{t('crm.quest.maisTextos', { count: l.length - MAX_TEXTOS })}</p>}
    </>
  );
  return (
    <section className={CLASSE_CARTAO} data-resumo-pergunta={p.id}>
      <header>
        <h3 className="font-semibold">{p.texto}</h3>
        <p className="text-xs text-tinta-3">{t('crm.quest.respondentes', { count: resumo.total })}</p>
      </header>
      {resumo.total === 0 ? (
        <p className="text-sm text-tinta-3">{t('crm.quest.semResposta')}</p>
      ) : resumo.tipo === 'opcoes' ? (
        <>
          <Barras linhas={resumo.opcoes} />
          {resumo.outros.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-tinta-2">
                {t('crm.quest.outrasRespostas')} ({resumo.outros.length})
              </p>
              {textos(resumo.outros)}
            </div>
          )}
        </>
      ) : resumo.tipo === 'escala' ? (
        <>
          <p className="text-sm font-medium" data-media="">
            {t('crm.quest.media', { valor: numero(resumo.media, 1) })}
          </p>
          <Barras linhas={resumo.valores.map((v, i) => ({ ...v, rotulo: i === 0 && p.escala?.rotulo_min ? `${v.rotulo} (${p.escala.rotulo_min})` : i === resumo.valores.length - 1 && p.escala?.rotulo_max ? `${v.rotulo} (${p.escala.rotulo_max})` : v.rotulo }))} />
        </>
      ) : resumo.tipo === 'numero' ? (
        <div className="flex flex-col gap-1 text-sm" data-media="">
          <p className="font-medium">{t('crm.quest.media', { valor: numero(resumo.media, casas(resumo.media)) })}</p>
          <p className="text-tinta-3">{t('crm.quest.minMax', { min: numero(resumo.min, casas(resumo.min)), max: numero(resumo.max, casas(resumo.max)) })}</p>
        </div>
      ) : (
        textos(resumo.textos)
      )}
    </section>
  );
}

function Individuais({ perguntas, itens }: { perguntas: Pergunta[]; itens: RespostaColetada[] }) {
  const { t } = useTranslation('pipeline');
  const [i, setI] = useState(0);
  const atual = itens[Math.min(i, itens.length - 1)]!;
  return (
    <div className="flex flex-col gap-3" data-respostas-individuais="">
      <div className="overflow-x-auto rounded-xl border border-borda bg-sup-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-tinta-3">
              <th className="px-4 py-2 font-medium">{t('crm.quest.negociacao')}</th>
              <th className="px-4 py-2 font-medium">{t('crm.quest.respondidoPor')}</th>
              <th className="px-4 py-2 font-medium">{t('crm.quest.respondidoEm')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('crm.quest.pontuacao')}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {itens.map((x, k) => (
              <tr key={x.id} className={`border-t border-borda ${k === i ? 'bg-acento-suave' : ''}`} data-resposta-individual={x.oportunidade_id}>
                <td className="px-4 py-2">
                  <Link to={caminhoDaNegociacao(x.oportunidade_id)} className="font-medium text-acento-forte hover:underline" aria-label={t('crm.quest.abrirFicha', { nome: textoLivre(x.oportunidade_titulo, x.parceiro_nome) })}>
                    {textoLivre(x.oportunidade_titulo, x.parceiro_nome)}
                  </Link>
                </td>
                <td className="px-4 py-2">{x.respondido_por_nome ? nomePessoa(x.respondido_por_nome) : <span className="text-tinta-3">{t('crm.quest.semNome')}</span>}</td>
                <td className="px-4 py-2 whitespace-nowrap">{dataHora(x.respondido_em)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{x.pontuacao ?? ''}</td>
                <td className="px-4 py-2 text-right">
                  <Botao tamanho="sm" variante="fantasma" aria-pressed={k === i} icone={<FileText className="size-4" />} onClick={() => setI(k)} data-ver-resposta="">
                    {t('crm.quest.abrir')}
                  </Botao>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section className={CLASSE_CARTAO} data-resposta-aberta={atual.oportunidade_id}>
        <header className="flex flex-wrap items-center gap-2">
          <Link to={caminhoDaNegociacao(atual.oportunidade_id)} className="flex-1 font-semibold text-acento-forte hover:underline">
            {textoLivre(atual.oportunidade_titulo, atual.parceiro_nome)}
          </Link>
          <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.anterior')} icone={<ChevronLeft className="size-4" />} disabled={i === 0} onClick={() => setI((k) => k - 1)} />
          <span className="text-xs text-tinta-3">{t('crm.quest.posicao', { n: i + 1, total: itens.length })}</span>
          <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.quest.proxima')} icone={<ChevronRight className="size-4" />} disabled={i >= itens.length - 1} onClick={() => setI((k) => k + 1)} />
        </header>
        {atual.pontuacao !== null && <p className="text-sm text-tinta-2">{t('crm.quest.pontuacaoValor', { count: atual.pontuacao })}</p>}
        <dl className="flex flex-col gap-3">
          {perguntas.map((p) =>
            p.tipo === 'secao' ? (
              <h4 key={p.id} className="border-t border-borda pt-3 text-sm font-semibold">
                {p.texto}
              </h4>
            ) : (
              <div key={p.id}>
                <dt className="text-xs text-tinta-3">{p.texto}</dt>
                <dd className="text-sm">{valorLegivel(p, atual.respostas[p.id]) || <span className="text-tinta-3">-</span>}</dd>
              </div>
            ),
          )}
        </dl>
      </section>
    </div>
  );
}

function valorLegivel(p: Pergunta, v: Valor | undefined): string {
  if (p.tipo === 'data' && typeof v === 'string') return data(v);
  if ((p.tipo === 'numero' || p.tipo === 'escala') && typeof v === 'number') return numero(v, Number.isInteger(v) ? 0 : 2);
  return textoLivre(textoDoValor(v));
}

// ---------------------------------------------------------------------------------------------------------------- resposta na ficha

export interface QuestionarioDaNegociacao extends Omit<Questionario, 'respostas'> {
  respostas: Record<string, Valor>;
  respondido_em: string | null;
  respondido_por_nome?: string | null;
  pontuacao?: number | null;
}

/** Lista de 30/10 (item 14): uma rodada de um questionário na negociação; pode haver várias, iguais ou de modelos diferentes. */
export interface AplicacaoQuestionario {
  id: string;
  questionario_id: string;
  situacao: 'em_andamento' | 'concluido';
  respostas: Record<string, Valor>;
  pontuacao: number | null;
  criado_em: string;
  atualizado_em: string;
  concluido_em: string | null;
  respondido_por_nome: string | null;
}

/**
 * Uma aplicação do questionário na ficha da negociação: todos os tipos, a obrigatória marcada e conferida ao concluir (e no
 * servidor). "Salvar rascunho" guarda o que há sem exigir as obrigatórias. O estado de cada aplicação é só dela: as outras
 * abertas ao mesmo tempo não se perdem nem se misturam.
 */
/** `primario`: o "Concluir" é o botão principal da vista (RL301-VIS-B4: só uma aplicação por vez); sem ele fica secundário. */
export function ResponderQuestionario({ opId, q, aplicacao, podeEditar, primario = true, aoExcluir }: { opId: string; q: Omit<QuestionarioDaNegociacao, 'respostas' | 'respondido_em'>; aplicacao: AplicacaoQuestionario; podeEditar: boolean; primario?: boolean; aoExcluir?: () => void }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const [r, setR] = useState<Record<string, Valor>>(aplicacao.respostas ?? {});
  const [erros, setErros] = useState<Record<string, string>>({});
  const { ref } = useValidacaoLocal(erros);
  const concluida = aplicacao.situacao === 'concluido';
  const salvar = useMutation({
    mutationFn: ({ respostas, concluir }: { respostas: Record<string, Valor>; concluir: boolean }) => api('PUT', `/pipeline/oportunidades/${opId}/questionarios/aplicacoes/${aplicacao.id}`, { respostas, concluir }),
    onSuccess: async (_d, v) => {
      toast.sucesso(v.concluir ? t('crm.questionarios.salvo') : t('crm.questionarios.rascunhoSalvo'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'questionarios-da-negociacao', opId] });
      await qc.invalidateQueries({ queryKey: ['pipeline', 'resumo-negociacao', opId] });
      if (v.concluir) await qc.invalidateQueries({ queryKey: ['pipeline', 'questionario-respostas', q.id] });
      // X1: com a qualificação automática, as estrelas e o histórico da negociação podem ter mudado.
      if (v.concluir && q.qualificar_auto) {
        await qc.invalidateQueries({ queryKey: ['pipeline'] });
        await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', opId] });
      }
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const mudar = (id: string, v: Valor) => {
    setR((x) => ({ ...x, [id]: v }));
    if (erros[id])
      setErros((e) => {
        const { [id]: _fora, ...resto } = e;
        return resto;
      });
  };
  const enviar = (concluir: boolean) => {
    const c = conferirRespostas(q.perguntas, r);
    // Rascunho: só o tipo e a opção contam; a obrigatória fica para a conclusão.
    const msgs = Object.fromEntries(Object.entries(c).filter(([, v]) => concluir || v === 'invalida').map(([k, v]) => [k, v === 'obrigatoria' ? t('crm.quest.responda') : t('crm.quest.invalida')]));
    setErros(msgs);
    if (Object.keys(msgs).length) {
      toast.erro(t('crm.quest.corrigir'));
      return;
    }
    salvar.mutate({ respostas: respostasParaEnvio(q.perguntas, r), concluir });
  };
  const numeros = numeracao(q.perguntas);
  const pts = pontuacao(q.perguntas, r);
  const quando = concluida ? (aplicacao.concluido_em ?? aplicacao.atualizado_em) : aplicacao.atualizado_em;
  return (
    <form
      ref={ref}
      noValidate
      className="flex flex-col gap-4 rounded-xl border border-borda bg-sup-1 p-4"
      data-questionario={q.nome}
      data-aplicacao={aplicacao.id}
      data-situacao={aplicacao.situacao}
      onSubmit={(e) => {
        e.preventDefault();
        enviar(true);
      }}
    >
      {(q.descricao || pts !== null || aplicacao.respondido_por_nome) && (
        <header className="flex flex-col gap-1 text-sm">
          {q.descricao && <p className="whitespace-pre-line text-tinta-2">{textoLivre(q.descricao)}</p>}
          {aplicacao.respondido_por_nome && (
            <p className="text-xs text-tinta-3" data-respondido-por="">
              {t(concluida ? 'crm.quest.ultimaResposta' : 'crm.questionarios.alteradoPor', { nome: nomePessoa(aplicacao.respondido_por_nome), quando: dataHora(quando) })}
            </p>
          )}
          {pts !== null && (
            <p className="text-xs font-semibold text-acento-forte" data-pontuacao={pts}>
              {t('crm.quest.pontuacao')}: {t('crm.quest.pontuacaoValor', { count: pts })}
            </p>
          )}
        </header>
      )}
      {q.perguntas.some((p) => p.obrigatoria) && <p className="text-xs text-perigo">{t('crm.quest.obrigatoriaAviso')}</p>}
      {q.perguntas.map((p, i) =>
        p.tipo === 'secao' ? (
          <div key={p.id} className="border-t border-borda pt-3">
            <h3 className="font-semibold">{p.texto}</h3>
            {p.descricao && <p className="text-sm text-tinta-3">{textoLivre(p.descricao)}</p>}
          </div>
        ) : (
          <CampoResposta key={p.id} p={p} n={numeros[i]!} valor={r[p.id] ?? null} erro={erros[p.id]} desabilitado={!podeEditar} aoMudar={(v) => mudar(p.id, v)} />
        ),
      )}
      {podeEditar && (
        <div className="flex flex-wrap items-center gap-2">
          <Botao variante={primario ? 'primario' : 'secundario'} type="submit" carregando={salvar.isPending} data-salvar-questionario="">
            {concluida ? t('crm.questionarios.salvar') : t('crm.questionarios.concluir')}
          </Botao>
          {!concluida && (
            <Botao type="button" disabled={salvar.isPending} onClick={() => enviar(false)} data-salvar-rascunho="">
              {t('crm.questionarios.salvarRascunho')}
            </Botao>
          )}
          {aoExcluir && (
            <Botao type="button" variante="fantasma" icone={<Trash2 className="size-4" />} onClick={aoExcluir} data-excluir-aplicacao="" className="sm:ml-auto">
              {t('crm.questionarios.excluirAplicacao')}
            </Botao>
          )}
        </div>
      )}
    </form>
  );
}

function CampoResposta({ p, n, valor, erro, desabilitado, aoMudar }: { p: Pergunta; n: number; valor: Valor; erro?: string; desabilitado: boolean; aoMudar: (v: Valor) => void }) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const idErro = `${id}-erro`;
  const aria = { 'aria-invalid': erro ? true : undefined, 'aria-describedby': erro ? idErro : undefined } as const;
  const [numTexto, setNumTexto] = useState(typeof valor === 'number' ? numeroEditavel(valor) : '');
  const rotulo = (
    <>
      <span className="text-sm font-medium">
        {p.texto}
        {p.obrigatoria && <span className="text-perigo"> *</span>}
      </span>
      {p.descricao && <span className="block text-xs text-tinta-3">{textoLivre(p.descricao)}</span>}
    </>
  );
  const rodape = erro && (
    <p id={idErro} role="alert" className="text-xs text-perigo">
      {erro}
    </p>
  );
  // Escolha única com "Outro": o texto livre fica no valor quando não é uma das opções.
  const ehOutro = typeof valor === 'string' && valor !== '' && !p.opcoes.includes(valor);
  const [outroTexto, setOutroTexto] = useState(() => (Array.isArray(valor) ? (valor.find((x) => !p.opcoes.includes(x)) ?? '') : ehOutro ? (valor as string) : ''));
  const [outroMarcado, setOutroMarcado] = useState(() => (Array.isArray(valor) ? valor.some((x) => !p.opcoes.includes(x)) : ehOutro));

  if (p.tipo === 'multipla' || p.tipo === 'caixas') {
    const multi = p.tipo === 'caixas';
    const lista = Array.isArray(valor) ? valor : [];
    const marcar = (o: string, sim: boolean) => {
      if (!multi) return aoMudar(sim ? o : null);
      const base = lista.filter((x) => p.opcoes.includes(x) && x !== o);
      const novas = sim ? [...base, o] : base;
      aoMudar([...p.opcoes.filter((x) => novas.includes(x)), ...(outroMarcado && outroTexto.trim() ? [outroTexto] : [])]);
    };
    const mudarOutro = (marcado: boolean, texto: string) => {
      setOutroMarcado(marcado);
      setOutroTexto(texto);
      if (multi) aoMudar([...lista.filter((x) => p.opcoes.includes(x)), ...(marcado && texto.trim() ? [texto] : [])]);
      else aoMudar(marcado ? texto : null);
    };
    const tipoInput = multi ? 'checkbox' : 'radio';
    const marcadaUnica = (o: string) => !outroMarcado && valor === o;
    return (
      <fieldset className="flex flex-col gap-2" data-campo-resposta={p.id} data-tipo={p.tipo} {...aria}>
        <legend className="mb-1">{rotulo}</legend>
        {p.opcoes.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type={tipoInput} name={id} className="size-4 accent-acento" disabled={desabilitado} checked={multi ? lista.includes(o) : marcadaUnica(o)} onChange={(e) => {
              if (!multi) setOutroMarcado(false);
              marcar(o, e.target.checked);
            }} />
            {o}
          </label>
        ))}
        {p.outro && (
          <label className="flex items-center gap-2 text-sm">
            <input type={tipoInput} name={id} className="size-4 accent-acento" disabled={desabilitado} checked={outroMarcado} onChange={(e) => mudarOutro(e.target.checked, outroTexto)} data-marcar-outro="" />
            <span>{t('crm.quest.outro')}:</span>
            <Entrada aria-label={t('crm.quest.outroPlaceholder')} maxLength={300} className="flex-1" disabled={desabilitado} placeholder={t('crm.quest.outroPlaceholder')} value={outroTexto} onChange={(e) => mudarOutro(true, e.target.value)} data-texto-outro="" />
          </label>
        )}
        {!multi && !p.obrigatoria && !desabilitado && (valor !== null || outroMarcado) && (
          <button type="button" className="self-start text-xs text-tinta-3 hover:underline" onClick={() => mudarOutro(false, '')}>
            {t('crm.quest.limpar')}
          </button>
        )}
        {rodape}
      </fieldset>
    );
  }
  if (p.tipo === 'escala') {
    const e = p.escala ?? { min: 1, max: 5, rotulo_min: '', rotulo_max: '' };
    const valores = Array.from({ length: e.max - e.min + 1 }, (_, k) => e.min + k);
    return (
      <fieldset className="flex flex-col gap-2" data-campo-resposta={p.id} data-tipo={p.tipo} {...aria}>
        <legend className="mb-1">{rotulo}</legend>
        <div className="flex flex-wrap items-end gap-3 text-sm">
          {e.rotulo_min && <span className="pb-1 text-tinta-3">{e.rotulo_min}</span>}
          {valores.map((v) => (
            <label key={v} className="flex flex-col items-center gap-1">
              <span className="text-xs text-tinta-2">{v}</span>
              <input type="radio" name={id} className="size-4 accent-acento" aria-label={t('crm.quest.escalaRotulo', { pergunta: p.texto, n: v })} disabled={desabilitado} checked={valor === v} onChange={() => aoMudar(v)} />
            </label>
          ))}
          {e.rotulo_max && <span className="pb-1 text-tinta-3">{e.rotulo_max}</span>}
        </div>
        {rodape}
      </fieldset>
    );
  }
  const campo =
    p.tipo === 'paragrafo' ? (
      <textarea id={id} rows={3} maxLength={2000} className={CLASSE_AREA} disabled={desabilitado} value={typeof valor === 'string' ? valor : ''} onChange={(e) => aoMudar(e.target.value)} {...aria} />
    ) : p.tipo === 'lista' ? (
      <Selecao id={id} disabled={desabilitado} value={typeof valor === 'string' ? valor : ''} vazio={t('crm.form.selecionar')} opcoes={p.opcoes.map((o) => ({ valor: o, rotulo: o }))} onChange={(e) => aoMudar(e.target.value || null)} {...aria} />
    ) : p.tipo === 'data' || p.tipo === 'hora' ? (
      <CampoData id={id} type={p.tipo === 'data' ? 'date' : 'time'} disabled={desabilitado} value={typeof valor === 'string' ? valor : ''} onChange={(e) => aoMudar(e.target.value || null)} {...aria} />
    ) : p.tipo === 'numero' ? (
      <Entrada
        id={id}
        inputMode="decimal"
        disabled={desabilitado}
        value={numTexto}
        onChange={(e) => {
          setNumTexto(e.target.value);
          aoMudar(e.target.value.trim() === '' ? null : (paraNumero(e.target.value) ?? Number.NaN));
        }}
        {...aria}
      />
    ) : (
      <Entrada id={id} maxLength={300} disabled={desabilitado} value={typeof valor === 'string' ? valor : ''} onChange={(e) => aoMudar(e.target.value)} {...aria} />
    );
  return (
    <div className="flex flex-col gap-1" data-campo-resposta={p.id} data-tipo={p.tipo} data-numero={n}>
      <label htmlFor={id}>{rotulo}</label>
      <div className="md:w-2/3">{campo}</div>
      {rodape}
    </div>
  );
}
